import {computed, Signal, signal, WritableSignal} from '@angular/core';
import {CycleStatusType, FieldType, ICompanySettings, ICycle, IField, UserModel, Policy, StationStatusType, Stamp, IFieldSettings} from '@core/api/data-access'; // Import IFieldSettings
import {CycleLogsModel, CycleModel, FieldSettingsModel} from '@cycles/data-access'; // Import FieldSettingsModel
import {FieldModelExtensionsUtils} from '@cycles/utils';
import {ISignCycle, ISignService, ISignStation, SignIndicatorsStruct, StoredFile} from '@global-data';
import {ValidationRuleModel} from '@validations/validation-rules/data-access';

import {SignService} from '../../services';
import {SignDocumentModel} from '../document';
import {SignFieldModel, TextBoxSignFieldModel} from '../fields';
import {SignFieldModelFactory} from '../fields/sign-field.model-factory';
import {SignStationModel} from '../station';

const isDigitalSignatureField = FieldModelExtensionsUtils.isDigitalSignatureField;

export class SignCycleModel extends CycleModel implements ISignCycle {
	signServiceRef: ISignService;
	logs: CycleLogsModel | null = null;

	constructor(
		init: Partial<SignCycleModel | ICycle>,
		public signerId: string,
		signService: SignService,
	) {
		super(init);
		this.signServiceRef = signService;
		if (this.stations) {
			this.stations$.set(this.stations.map(station => new SignStationModel(station)));
		} else {
			this.stations$.set([]);
		}
		if (this.log) {
			this.logs = new CycleLogsModel(this.log);
		} else {
			this.logs = null;
		}
	}

	// --- ( initSignIndicators and other parts remain unchanged ) ---
	public initSignIndicators(activeUser: UserModel, isPermanentSigner: boolean, companySettings: ICompanySettings, internalFields: SignFieldModel[], stamps: Stamp[]): void {
		const indicatorsToUpload: SignIndicatorsStruct = this.signCycleIndicators(); // Get current value
		const currentLogs = this.logs; // Use the initialized logs model

		// Check if logs model is available before accessing its signals
		if (!currentLogs) {
			console.warn('Cannot initialize sign indicators: CycleLogsModel is null.');
			// Set default values or handle the error appropriately
			indicatorsToUpload.hasOpeningMessage = false;
			indicatorsToUpload.hasCycleComment = false;
			// ... set other defaults if needed ...
			this.signCycleIndicators.set(indicatorsToUpload); // Update the signal
			return; // Exit if no logs
		}

		/* -------------- Permanent Signer: -------------- */
		indicatorsToUpload.isPermanentSigner = isPermanentSigner;

		/* --------------  Comment Policy: -------------- */
		// (Logic remains the same)
		switch (companySettings.commentPolicy) {
			case Policy.NoOne:
				indicatorsToUpload.canCommentCycle = false;
				break;
			case Policy.Everyone:
			case Policy.UsersOnly: // Combine cases if logic is identical
				indicatorsToUpload.canCommentCycle = true;
				indicatorsToUpload.autoOpenCommentDialog = companySettings.autoRemarks;
				break;
		}

		/* --------------- Reject Policy: --------------- */
		// (Logic remains the same)
		switch (companySettings.rejectPolicy) {
			case Policy.NoOne:
				indicatorsToUpload.canRejectCycle = false;
				break;
			case Policy.Everyone:
				indicatorsToUpload.canRejectCycle = true;
				break;
			case Policy.UsersOnly:
				indicatorsToUpload.canRejectCycle = isPermanentSigner;
				break;
		}

		/* ---------------  Alert Policy: --------------- */
		indicatorsToUpload.canAlertCycle = companySettings.defaultAllowAlert;

		/* ---------------  Splash Screen: --------------- */
		indicatorsToUpload.openSplashScreen = companySettings.useSplashScreen;
		// **Correction for Error 5:** Access signal value
		indicatorsToUpload.hasOpeningMessage = Boolean(currentLogs.openingMessage$());
		// **Correction for Error 6:** Access signal value
		indicatorsToUpload.hasCycleComment = currentLogs.cycleHasComments$();

		/* ---------------  Form Filler: --------------- */
		// (Logic remains the same)
		const allFillerTypesHidden: boolean = companySettings.fieldFillerHiddenTypes?.length === 6; // Check exact number if needed
		indicatorsToUpload.canShowFormFillerBtn = !allFillerTypesHidden && internalFields.length > 0;
		indicatorsToUpload.autoOpenFormFiller = companySettings.fieldFillerAuto && indicatorsToUpload.canShowFormFillerBtn && typeof window !== 'undefined' && !!window.navigator.userAgent.match(/Mobi/i); // Safer mobile check

		/* ---------------  Sign In Click: --------------- */
		// (Logic remains the same, added null checks)
		if (companySettings.autoApproveCycle && isPermanentSigner) {
			const hasDefaultStamp: boolean = !!stamps?.length && (activeUser?.preferences?.defaultStamp ?? 1) >= 2; // Safe access
			if (hasDefaultStamp) {
				const signatureFieldsWithoutStamps = internalFields?.filter((field: SignFieldModel) => field?.type === FieldType.signature) ?? [];
				const hasNotSignatureRequiredFields: boolean = internalFields?.some((field: SignFieldModel) => field?.type !== FieldType.signature && field?.required) ?? false;
				indicatorsToUpload.canSignInClick = signatureFieldsWithoutStamps.length > 0 && !hasNotSignatureRequiredFields;
			} else {
				indicatorsToUpload.canSignInClick = false; // Ensure it's false if no default stamp
			}
		} else {
			indicatorsToUpload.canSignInClick = false; // Ensure it's false if conditions not met
		}

		/* --------------------------------- GTM ID --------------------------------- */
		indicatorsToUpload.googleTagManager = companySettings?.gtmId;

		/* -------------------------------------- Assign indicators to signal: -------------------------------------- */
		this.signCycleIndicators.set(indicatorsToUpload);
	}

	// Make signal initialization safer
	signCycleIndicators: WritableSignal<SignIndicatorsStruct> = signal({
		isPermanentSigner: false,
		autoOpenCommentDialog: false,
		canAlertCycle: false,
		canCommentCycle: false,
		canRejectCycle: false,
		canShowInspectorButtons: false, // Consider if this needs initialization logic
		autoOpenFormFiller: false,
		canShowFormFillerBtn: false,
		canSignInClick: false,
		hasRequiredGpsFields: false, // Consider if this needs initialization logic
		openSplashScreen: false,
		hasOpeningMessage: false,
		googleTagManager: undefined,
		hasCycleComment: false,
	});

	/* ------------------------------------------------  Station ------------------------------------------------ */
	public readonly stations$: WritableSignal<ISignStation[]> = signal([]); // Initialize empty

	public readonly pastStations$: Signal<ISignStation[]> = computed(() => {
		const stations = this.stations$();
		const pastStationStatuses: StationStatusType[] = [
			StationStatusType.Approved,
			StationStatusType.Rejected,
			StationStatusType.Alert,
			StationStatusType.TimedOut,
			// Add Completed, Canceled if they are considered 'past'
		];
		return stations.filter(station => station?.status && pastStationStatuses.includes(station.status));
	});

	public readonly activeStation$: Signal<ISignStation | undefined> = computed(() =>
		this.stations$()?.find(station => station?.status === StationStatusType.InProcess && station?.users?.some(user => user?.user === this.signerId)),
	);

	public readonly futureStations$: Signal<ISignStation[]> = computed(() => {
		const stations = this.stations$();
		return stations.filter(station => station?.status === StationStatusType.Waiting);
	});

	/* ------------------------------------------------- Signer ------------------------------------------------- */
	public canSign(userId: string): boolean {
		const activeStation = this.activeStation$(); // Get computed value
		if (!activeStation) return false;
		// Check cycle status and user presence in active station
		return this.status === CycleStatusType.Cycling && activeStation.users?.some(user => user?.user === userId);
	}

	/* ------------------------------------------------ Document ------------------------------------------------ */
	private _documentModel: WritableSignal<SignDocumentModel | null> = signal(null); // Allow null
	public documentModel: Signal<SignDocumentModel | null> = this._documentModel.asReadonly();

	setDocument(document: StoredFile | null): void {
		// Allow null document
		this._documentModel.set(document ? new SignDocumentModel(document, this) : null);
	}

	/* ------------------------------------------------- Fields ------------------------------------------------- */
	private _fields: WritableSignal<SignFieldModel[]> = signal([]); // Initialize empty
	public fields: Signal<SignFieldModel[]> = this._fields.asReadonly();

	public addField(...fields: SignFieldModel[]): void {
		if (fields && fields.length > 0) {
			this._fields.update(currentFields => [...currentFields, ...fields.filter(Boolean)]); // Filter out potential null/undefined
		}
	}

	public setFields(fields: IField[] | null, override: boolean): {needValidationRules: boolean} {
		// Allow null fields
		let fieldsToSet: SignFieldModel[] = (fields ?? []).flatMap((field: IField) => {
			if (!field) return [];
			if (field instanceof SignFieldModel) return [field];
			const signField = SignFieldModelFactory(field, this.signServiceRef, this.documentModel()?.pagesCount);
			if (!signField || isDigitalSignatureField(signField)) return [];
			return [signField as SignFieldModel];
		});

		const ordinals = new Set<number>();
		const withoutOrdinals: SignFieldModel[] = [];
		fieldsToSet.forEach(field => {
			// Get the current settings value
			const currentSettings = field.settings$();
			const ordinal = currentSettings?.Ordinal;
			if (ordinal !== undefined && ordinal !== null && !isNaN(ordinal)) {
				ordinals.add(ordinal);
			} else {
				withoutOrdinals.push(field);
			}
		});

		if (withoutOrdinals.length > 0) {
			let nextOrdinal = 0;
			withoutOrdinals.forEach(field => {
				while (ordinals.has(nextOrdinal)) {
					nextOrdinal++;
				}
				// **Correction for Error TS2741:** Ensure the object passed to update has all required properties of FieldSettingsModel,
				// or create a new FieldSettingsModel instance.
				// Assuming FieldSettingsModel constructor can handle partial data or the settings signal holds a valid instance.
				field.settings$.update(settings => {
					// Option 1: If settings is guaranteed to be a valid FieldSettingsModel instance
					// settings.Ordinal = nextOrdinal; return settings; // This might not trigger change detection if not immutable
					// Option 2: Create a new object/instance (safer for immutability)
					const newSettingsData: Partial<IFieldSettings> = {...settings, Ordinal: nextOrdinal};
					// If FieldSettingsModel has a constructor accepting partial data and computes missing fields:
					// return new FieldSettingsModel(newSettingsData);
					// Or if FieldSettingsModel is just an interface/type, return the plain object:
					return newSettingsData as FieldSettingsModel; // Cast needed if returning plain object for WritableSignal<FieldSettingsModel>
				});
				ordinals.add(nextOrdinal);
			});
		}

		fieldsToSet = fieldsToSet.sort((a, b) => (a.settings$()?.Ordinal ?? Infinity) - (b.settings$()?.Ordinal ?? Infinity));

		if (override) {
			this._fields.set(fieldsToSet);
		} else {
			this._fields.update(currentFields => [...currentFields, ...fieldsToSet]);
		}

		const needValidationRules = fieldsToSet.some(field => field?.validationRuleData());
		return {needValidationRules};
	}

	public getField(index: number): SignFieldModel | undefined {
		return this.fields().find(field => field?.index === index);
	}

	public setFieldsValidationRules(validationRules: Map<string, ValidationRuleModel> | null): void {
		if (!validationRules) return;

		this._fields.update((currentFields: SignFieldModel[]) => {
			return currentFields.map((f: SignFieldModel) => {
				if (!f) return f;
				if (f.type === FieldType.textBox) {
					const field = f as TextBoxSignFieldModel;
					const ruleData = field.validationRuleData();
					if (ruleData?.validationRule === null) {
						const validationRuleKey = ruleData.key;
						if (validationRuleKey && validationRules.has(validationRuleKey)) {
							const validationRule = validationRules.get(validationRuleKey);
							if (validationRule?.validator) {
								ruleData.setValidationRule(validationRule);
								field.initBaseValidators();
							} else {
								console.warn(`Validation rule found for key '${validationRuleKey}', but it lacks a validator.`);
							}
						}
					}
				}
				return f;
			});
		});
	}
}
